/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { CurrentUserParameters } from "features/userParameters/UserParametersSchemata"
import _ from "lodash"

import { describeCohort, DRAWDOWN_COHORTS, filterCohorts, makeCapeCohorts } from "./cohorts"
import { makeConsumptionRateRange } from "./consumptionRates"
import { computeFinalValuesDistribution, getMedianAnnualConsumptionRate } from "./finalValues"
import { HistoricalDataFilters } from "./HistoricalDataFilters"
import {
  Cohort,
  FAILURE_RATES,
  FinalValuesDistribution,
  InitialScrsTable,
  NumericTable,
  UiTable,
} from "./types"
import {
  computeConsumptionRatesForSelectedCohorts,
  computeFailureRatesForSelectedCohorts,
  formatCurrency,
  formatPercentage,
} from "./utils"
import { DeepReadonly } from "utils/readonly"

type ReadonlyCohort = DeepReadonly<Cohort>
type ReadonlyInitialScrsTable = DeepReadonly<InitialScrsTable>
type ReadonlyNumericTable = DeepReadonly<NumericTable>
// AIDEV-NOTE: Keep cohort table data in tuple entries to avoid extra map/array materialization while combining tables.
type CohortRateTableEntry = readonly [cohort: ReadonlyCohort, table: ReadonlyNumericTable]

export interface Results {
  capeFailureRates: UiTable
  drawdownFailureRates: UiTable
  capeConsumptionRates: UiTable
  drawdownConsumptionRates: UiTable
  capeConsumptionAmounts: UiTable
  drawdownConsumptionAmounts: UiTable
  userFiltersRetirementCount: number
  finalValuesDistribution: FinalValuesDistribution
  maybeUserConsumptionAmountResults?: {
    capeFailureRates: UiTable
    drawdownFailureRates: UiTable
  }
}

// Column header labels
export const FAILURE_RATE_COL = "Failure Rate"
export const CONSUMPTION_RATE_COL = "Initial Consumption Rate"
export const CONSUMPTION_AMOUNT_COL = "Initial Consumption (p.a.)"
export const CONSUMPTION_RATE_DECIMALS = 10

function formatPctTable(table: DeepReadonly<UiTable>): UiTable {
  const result: Record<string, string[]> = {}
  for (const [column, values] of Object.entries(table)) {
    // only map numbers through formatPercentage
    if (values.every(v => typeof v === "number")) {
      result[column] = values.map(formatPercentage)
    } else {
      // preserve non-numeric columns as-is (or turn everything to strings)
      result[column] = values.map(v => String(v))
    }
  }
  return result
}

/**
 * Compute per-cohort failure-and-consumption rate tables.
 */
export function computeFailureAndConsumptionRates(
  failureRates: readonly number[],
  consumptionRates: readonly number[],
  cohort: ReadonlyCohort,
  initialScrs: ReadonlyInitialScrsTable
): {
  failureRateTable: NumericTable
  consumptionRateTable: NumericTable
  retirementCount: number
} {
  // Process each cohort and add a column to each table

  const filteredInitialScrs = filterCohorts(initialScrs, cohort)

  const consumptionRateTable = computeConsumptionRatesForSelectedCohorts(
    filteredInitialScrs,
    failureRates
  )
  const failureRateTable = computeFailureRatesForSelectedCohorts(
    filteredInitialScrs,
    consumptionRates
  )

  return {
    failureRateTable,
    consumptionRateTable,
    retirementCount: filteredInitialScrs.initial_scr.length,
  }
}

/**
 * Transpose a per-cohort rates map into a UiTable.
 * If isFailureRateTable, the first column is consumption thresholds;
 * otherwise it's failure rates.
 */
export function makeCombinedRateTable(
  ratesByCohortEntries: readonly CohortRateTableEntry[],
  isFailureRateTable: boolean
): NumericTable {
  // Determine the header for the first (threshold) column
  const firstColKey = isFailureRateTable ? CONSUMPTION_RATE_COL : FAILURE_RATE_COL

  // Grab the first cohort's single‐row table to discover the threshold keys
  if (ratesByCohortEntries.length === 0) {
    return {}
  }
  const firstEntry = ratesByCohortEntries[0]
  const [, firstTable] = firstEntry

  // The keys of that table are the (stringified) rates
  const headers = Object.keys(firstTable)

  // Build the transposed result
  const result: NumericTable = {
    // Parse the threshold strings into numbers
    [firstColKey]: headers.map(ts => parseFloat(ts)),
  }

  // For each cohort, pull out the single‐value arrays and align by threshold order
  for (const [cohort, cohortTable] of ratesByCohortEntries) {
    const cohortTableRecord = cohortTable as Record<string, readonly number[] | number>
    result[cohort.description ?? describeCohort(cohort)] = headers.map<number>(ts => {
      const arr: readonly number[] | number = cohortTableRecord[ts]
      return typeof arr === "number" ? arr : arr[0]
    })
  }

  return result
}

/**
 * Combines individual single-row rate tables from multiple cohorts into a
 * single, transposed UI table of consumption amounts scaled by the initial
 * portfolio dollars.
 *
 * Steps:
 *   1. Concatenates individual cohort tables and transposes them so thresholds become rows.
 *   2. Labels the first column as "Initial Consumption Rate" or "Failure Rate" based on the flag.
 *   3. Formats the threshold values by multiplying by 100, rounding to two decimals,
 *      and appending "%" to create percentage strings.
 *   4. For each cohort column, multiplies each rate threshold by the initial portfolio dollars,
 *      rounds to the nearest integer, and formats as currency strings.
 *
 * @param ratesByCohort Map<Cohort, NumericTable> where each table has a single-row
 *        NumericTable mapping threshold strings to numeric rate values.
 * @param initialPortfolioDollars The portfolio value used to scale rates into dollar amounts.
 * @param isFailureRateTable If true, the first column label is "Initial Consumption Rate";
 *        otherwise, it's "Failure Rate".
 * @returns A UiTable where the first column contains percentage-formatted thresholds,
 *          and each subsequent column contains currency-formatted amounts per cohort.
 */
export function makeCombinedAmountTable(
  ratesByCohortEntries: readonly CohortRateTableEntry[],
  initialPortfolioDollars: number,
  isFailureRateTable: boolean
): UiTable {
  const firstColKey = isFailureRateTable ? CONSUMPTION_RATE_COL : FAILURE_RATE_COL

  // Extract threshold keys from the first cohort's table
  if (ratesByCohortEntries.length === 0) {
    return {}
  }
  const firstEntry = ratesByCohortEntries[0]
  const [, firstTable] = firstEntry
  const thresholdKeys = Object.keys(firstTable)

  // Build the first column: formatted percentage thresholds
  const table: UiTable = {
    [firstColKey]: thresholdKeys.map(ts => {
      const pct = parseFloat(ts) * 100
      // Round to two decimal places (banker's rounding equivalent)
      const rounded = Math.round(pct * 100) / 100

      //   * keep up to two decimals
      //   * remove trailing zeros
      //   * ensure at least one decimal digit remains
      let str = rounded.toFixed(2).replace(/0+$/, "") // drop extra zeros
      if (str.endsWith(".")) {
        str += "0" // keep a single decimal if none left
      }

      return `${str}%`
    }),
  }

  // Build each cohort column: formatted currency amounts
  for (const [cohort, cohortTable] of ratesByCohortEntries) {
    const cohortTableRecord = cohortTable as Record<string, readonly number[] | number>
    table[cohort.description ?? describeCohort(cohort)] = thresholdKeys.map(ts => {
      const raw: readonly number[] | number = cohortTableRecord[ts]
      const rate = typeof raw === "number" ? raw : raw[0]
      const amount = Math.round(rate * initialPortfolioDollars)
      return formatCurrency(amount)
    })
  }

  return table
}

/**
 * Top-level to produce the three tables in one go.
 */
export function makeFailureRateAndConsumptionTables(
  failureRates: readonly number[],
  consumptionRates: readonly number[],
  cohorts: readonly ReadonlyCohort[],
  initialPortfolioDollars: number,
  initialScrs: ReadonlyInitialScrsTable
): {
  failureRateTable: NumericTable
  consumptionRateTable: NumericTable
  consumptionAmountTable: UiTable
  retirementCountsByCohort: Map<ReadonlyCohort, number>
} {
  const failureRateEntries: CohortRateTableEntry[] = []
  const consumptionRateEntries: CohortRateTableEntry[] = []
  const retirementCountsByCohort = new Map<ReadonlyCohort, number>()

  for (const cohort of cohorts) {
    const { failureRateTable, consumptionRateTable, retirementCount } =
      computeFailureAndConsumptionRates(failureRates, consumptionRates, cohort, initialScrs)

    retirementCountsByCohort.set(cohort, retirementCount)

    if (
      Object.keys(failureRateTable).length > 0 &&
      !Object.values(failureRateTable).some((values: readonly number[]) =>
        values.some(v => isNaN(v))
      )
    ) {
      failureRateEntries.push([cohort, failureRateTable])
    }

    if (
      Object.keys(consumptionRateTable).length > 0 &&
      !Object.values(consumptionRateTable).some((values: readonly number[]) =>
        values.some(v => isNaN(v))
      )
    ) {
      consumptionRateEntries.push([cohort, consumptionRateTable])
    }
  }

  // Combine into wide tables, or return empty objects if no data
  let failureRateTable: NumericTable
  if (failureRateEntries.length > 0) {
    // First column is the consumption‐rate thresholds
    failureRateTable = makeCombinedRateTable(failureRateEntries, /* isFailureRateDf: */ true)
  } else {
    failureRateTable = {}
  }

  let consumptionRateTable: NumericTable
  let consumptionAmountTable: UiTable
  if (consumptionRateEntries.length > 0) {
    // First column is the failure‐rate thresholds
    consumptionRateTable = makeCombinedRateTable(
      consumptionRateEntries,
      /* isFailureRateDf: */ false
    )
    // Then scale those rates into dollar amounts
    consumptionAmountTable = makeCombinedAmountTable(
      consumptionRateEntries,
      initialPortfolioDollars,
      /* isFailureRateTable: */ false
    )
  } else {
    consumptionRateTable = {}
    consumptionAmountTable = {}
  }

  return {
    failureRateTable,
    consumptionRateTable,
    consumptionAmountTable,
    retirementCountsByCohort,
  }
}

/*
 * Filters the failureRateTable for the row matching the user's consumption rate,
 * then prepends a dollar-amount column and reorders columns.
 */
export function makeAmountBasedFailureRateData(
  userInitialConsumptionAmount: number,
  userInitialConsumptionRate: number,
  failureRateTable: ReadonlyNumericTable
): UiTable {
  const roundedRate = _.round(userInitialConsumptionRate, CONSUMPTION_RATE_DECIMALS)

  // AIDEV-NOTE: The combined table has no threshold column when every cohort was
  // dropped for matching no rows - the one caller-visible consequence of the
  // empty-cohort NaN sentinel (issue #532). Tested by key presence rather than an
  // undefined check on the indexed value, because NumericTable indexes as a total
  // Record: the value is typed non-undefined even when the key is absent.
  if (!(CONSUMPTION_RATE_COL in failureRateTable)) {
    return {}
  }

  // 2) Find the index in the numeric "Initial Consumption Rate" column
  const rowIndex = failureRateTable[CONSUMPTION_RATE_COL].findIndex(
    r => _.round(r, CONSUMPTION_RATE_DECIMALS) === roundedRate
  )

  if (rowIndex === -1) {
    // no matching row → empty result
    return {}
  }

  // 3) Format the dollar amount
  const amountStr = formatCurrency(userInitialConsumptionAmount)

  // 4) Format the rounded consumption‐rate as a percentage string
  const rateStr = formatPercentage(roundedRate)

  // 5) Assemble the result: amount first, then the consumption‐rate, then every other numeric column
  const result: UiTable = {
    [CONSUMPTION_AMOUNT_COL]: [amountStr],
    [CONSUMPTION_RATE_COL]: [rateStr],
  }

  for (const col of Object.keys(failureRateTable)) {
    if (col === CONSUMPTION_RATE_COL) continue
    const val = failureRateTable[col][rowIndex]
    // convert each numeric failure‐rate to a percentage string
    result[col] = [formatPercentage(val)]
  }

  return result
}

/**
 * Computes all results (CAPE and drawdown variants, and final values distribution).
 */
export function computeResults(
  userParameters: DeepReadonly<CurrentUserParameters>,
  historicalRetirementFilters: DeepReadonly<HistoricalDataFilters>,
  initialScrsTable: ReadonlyInitialScrsTable
): Results {
  // derive the user's implied annual consumption rate, if any
  const userInitialAnnualConsumptionRate: number | null =
    userParameters.initialConsumptionPerAnnumDollars != null
      ? userParameters.initialConsumptionPerAnnumDollars / userParameters.initialPortfolioDollars
      : null

  // Create the user's filtered cohort
  const userCohort: ReadonlyCohort = {
    description: "Your Filters",
    firstStartingYear: historicalRetirementFilters.firstStartingYear,
    lastStartingYear: historicalRetirementFilters.lastStartingYear,
    cape: {
      ern: historicalRetirementFilters.cape === "ern",
      minValue: historicalRetirementFilters.minCape,
      maxValue: historicalRetirementFilters.maxCape,
    },
    minAbsDrawdown: historicalRetirementFilters.minAbsDrawdown,
    maxAbsDrawdown: historicalRetirementFilters.maxAbsDrawdown,
  }

  // generate the consumption‐rate grid
  const consumptionRates = makeConsumptionRateRange(
    initialScrsTable,
    userInitialAnnualConsumptionRate
  )

  // build cohort lists
  const capeCohorts: readonly ReadonlyCohort[] = [
    userCohort,
    ...makeCapeCohorts(historicalRetirementFilters.cape === "ern"),
  ]
  const drawdownCohorts: readonly ReadonlyCohort[] = [userCohort, ...DRAWDOWN_COHORTS]

  // compute the three tables for CAPE cohorts
  const {
    failureRateTable: capeFailureRateTable,
    consumptionRateTable: capeConsumptionRateTable,
    consumptionAmountTable: capeConsumptionAmountTable,
    retirementCountsByCohort: capeRetirementCountsByCohort,
  } = makeFailureRateAndConsumptionTables(
    FAILURE_RATES,
    consumptionRates,
    capeCohorts,
    userParameters.initialPortfolioDollars,
    initialScrsTable
  )

  // compute the three tables for drawdown cohorts
  const {
    failureRateTable: drawdownFailureRateTable,
    consumptionRateTable: drawdownConsumptionRateTable,
    consumptionAmountTable: drawdownConsumptionAmountTable,
    retirementCountsByCohort: _drawdownRetirementCountsByCohort,
  } = makeFailureRateAndConsumptionTables(
    FAILURE_RATES,
    consumptionRates,
    drawdownCohorts,
    userParameters.initialPortfolioDollars,
    initialScrsTable
  )

  // if the user specified a consumption amount,
  // slice out their specific failure‐rate table
  let maybeUserConsumptionAmountResults:
    | {
        capeFailureRates: UiTable
        drawdownFailureRates: UiTable
      }
    | undefined = undefined
  if (
    userInitialAnnualConsumptionRate !== null &&
    userParameters.initialConsumptionPerAnnumDollars != null
  ) {
    const userCapeFailureRateTable = makeAmountBasedFailureRateData(
      userParameters.initialConsumptionPerAnnumDollars,
      userInitialAnnualConsumptionRate,
      capeFailureRateTable
    )
    const userDrawdownFailureRateTable = makeAmountBasedFailureRateData(
      userParameters.initialConsumptionPerAnnumDollars,
      userInitialAnnualConsumptionRate,
      drawdownFailureRateTable
    )

    maybeUserConsumptionAmountResults = {
      capeFailureRates: userCapeFailureRateTable,
      drawdownFailureRates: userDrawdownFailureRateTable,
    }
  }

  // Calculate the annual consumption rate to use for final values
  const effectiveAnnualConsumptionRate =
    userInitialAnnualConsumptionRate ?? getMedianAnnualConsumptionRate(initialScrsTable)

  // Calculate final values distribution
  const finalValuesDistribution = computeFinalValuesDistribution(
    initialScrsTable,
    userCohort,
    effectiveAnnualConsumptionRate,
    userParameters.initialPortfolioDollars
  )

  return {
    maybeUserConsumptionAmountResults,
    capeFailureRates: formatPctTable(capeFailureRateTable),
    capeConsumptionRates: formatPctTable(capeConsumptionRateTable),
    capeConsumptionAmounts: capeConsumptionAmountTable,
    drawdownFailureRates: formatPctTable(drawdownFailureRateTable),
    drawdownConsumptionRates: formatPctTable(drawdownConsumptionRateTable),
    drawdownConsumptionAmounts: drawdownConsumptionAmountTable,
    userFiltersRetirementCount: capeRetirementCountsByCohort.get(userCohort) ?? 0,
    finalValuesDistribution,
  }
}
